import { useEffect, useRef } from "react";
import Decimal from "decimal.js";

import { MemberEligibilityInfo } from "@/App/types";
import { PharmacyElementsList, PharmacyList } from "@/App/types/pharmacy";

import { Pharmacy } from "../api/pharmacy";
import { ServiceAttributes } from "../types/medicalBenefit";

export const formatDollars = (value: number | string): string => {
  let formatValue = value;

  if (typeof formatValue === "undefined" || formatValue === null) return "--";

  if (typeof formatValue === "string" && isNaN(Number(formatValue))) {
    return formatValue;
  }

  if (typeof formatValue === "string" || !isNaN(formatValue)) {
    formatValue = typeof formatValue === "string" ? parseFloat(formatValue) : formatValue;
    let negative = false;
    if (formatValue < 0) {
      formatValue = Math.abs(formatValue);
      negative = true;
    }

    // This is a bit odd, we should probably not be switching types and rather make this more explicit.
    let amount: number | string = formatValue;

    if (formatValue < 1e6) amount = formatValue.toFixed(2);
    else if (formatValue >= 1e6 && formatValue < 1e9) amount = (formatValue / 1e6).toFixed(2) + "MM";
    else if (formatValue >= 1e9 && formatValue < 1e12) amount = (formatValue / 1e9).toFixed(2) + "B";
    else if (formatValue >= 1e12) amount = (formatValue / 1e12).toFixed(1) + "T";

    if (negative) {
      return "-$" + amount?.toLocaleString();
    }
    return "$" + amount?.toLocaleString();
  }
  return formatValue?.toLocaleString();
};

export const capitalizeString = (name: string): string => {
  return name
    .toLowerCase()
    .split(" ")
    .map((string) => string.charAt(0).toUpperCase() + string.substring(1))
    .join(" ");
};

export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return phone;
  // Check if the input is of correct
  const match = phone.match(/^(1|)?(\d{3})-?(\d{3})-?(\d{4})$/);

  if (match) {
    const intlCode = match[1] ? "+1 " : "";
    return [intlCode, match[2], "-", match[3], "-", match[4]].join("");
  }

  return null;
};

export const convertUnderscoreToKebabCase = (str: string): string => str.split("_").join("-");

export const convertCapsToPascal = (str: string): string =>
  str
    .split(" ")
    .map((str) => (str ? str[0].toUpperCase() + str.slice(1).toLowerCase() : str))
    .join(" ");

// Custom React Hook to get previous props or state

export const usePrevious = (value: any): any => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const getClaimProviderName = (firstName?: string | null | undefined, lastNameOrOrganizationalName?: string | null | undefined) => {
  if (firstName) {
    return `${firstName} ${lastNameOrOrganizationalName}`;
  }
  return lastNameOrOrganizationalName || "";
};

// eslint-disable  @typescript-eslint/no-explicit-any
export const parseSearchParams = (searchParams: URLSearchParams): any => {
  // eslint-disable  @typescript-eslint/no-explicit-any
  const params: any = {};
  searchParams.forEach((v, k) => {
    params[k] = v;
  });

  return params;
};

// Calculate row number for FBP and Nearby Pharmacy
export const calculateRowNumber = (index: number, page: number, pharmacyList: PharmacyList[] | PharmacyElementsList[]): number => {
  const currentPage = page + 1;
  const lastRowNumber = currentPage * pharmacyList.length;
  const firstRowNumber = lastRowNumber - pharmacyList.length;
  const currentRowNumber = firstRowNumber + index;

  return currentRowNumber;
};

export const stripTrailingZeros = (numericString: string): string => {
  /*  values that come back from claims could contain multiple
    trailing 0's following standard. convert it to a number
    to strip it and return back a string.*/
  if (numericString) {
    return Number(numericString).toString();
  }
  return numericString;
};

export const formatRejectMessage = (message: string): string => {
  const stripSupplementalFieldsInError = new RegExp(/(Field\(s\).*)/);
  return message.replace(stripSupplementalFieldsInError, "");
};

export const getCustomerSupportNumber = (memberEligibilityInfo: MemberEligibilityInfo, experienceSupportNumber?: string): string => {
  // hacks! special case for BSW since there's no override for this number
  if (memberEligibilityInfo?.plan.client_id == "BSWCARE") {
    return "866-334-3141 or TTY 771";
  }

  // if group customer service phone number is provided use it, otherwise fallback to the plan support #
  let customerSupportNumber = memberEligibilityInfo?.plan.customer_service_phone_number;
  const groupCustomerSupportNumber = memberEligibilityInfo?.group.customer_service_phone_number;

  if (groupCustomerSupportNumber) {
    customerSupportNumber = groupCustomerSupportNumber;
  }

  if (experienceSupportNumber) {
    customerSupportNumber = experienceSupportNumber;
  }

  return customerSupportNumber;
};

export const formatAmount = (amount: string | number | null | undefined) => {
  if (typeof amount === "undefined" || amount === null || isNaN(parseFloat(amount as string))) {
    return "--";
  }
  return formatDollars(amount);
};

export const dollarValueOrBlank = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return "--";
  return formatDollars(value);
};

export const subtract = (amount1: string | number | null | undefined, amount2: string | number | null | undefined) => {
  if (typeof amount1 === "undefined" || amount1 === null || isNaN(parseFloat(amount1 as string))) {
    return "--";
  }
  if (typeof amount2 === "undefined" || amount2 === null || isNaN(parseFloat(amount2 as string))) {
    return "--";
  }

  const total = new Decimal(amount1).minus(new Decimal(amount2)).toFixed(2);

  if (total.includes("-")) {
    return formatDollars("0");
  }

  return formatDollars(total);
};

export const getRoundedPharmacyDistance = (pharmacy: Pharmacy) => {
  if (!pharmacy?.distance) return pharmacy?.distance;

  return Math.round(pharmacy.distance * 100) / 100;
};

export const getPharmacyDescription = (pharmacy: Pharmacy, milesAbbreviation: string) => {
  const overrideDescription = pharmacy.pharmacy_display_rules?.description;
  if (overrideDescription) {
    return overrideDescription;
  } else {
    if (pharmacy.distance) {
      return `${getRoundedPharmacyDistance(pharmacy)} ${milesAbbreviation} | ${pharmacy.address_line_1 || ""} ${pharmacy.address_line_2 || ""}`;
    } else {
      return `${pharmacy.address_line_1 || ""} ${pharmacy.address_line_2 || ""}`;
    }
  }
};

export const determineMedicalCopays = (memberEligibilityInfo: MemberEligibilityInfo, copayType: "Physician Visit" | "Specialist Visit") => {
  const services = memberEligibilityInfo?.medical_plan_benefit?.services;
  if (services && "in_network_tier_1" in services) {
    const inNetworkTierOneServices = services["in_network_tier_1"];
    if (inNetworkTierOneServices && copayType in inNetworkTierOneServices) {
      const pcpVisitService = inNetworkTierOneServices[copayType];
      const attributes = pcpVisitService?.attributes as ServiceAttributes;
      const costShares = attributes?.cost_shares || [];

      const matchedPlaceOfService = costShares.find((costShare) => {
        return costShare.place_of_service.includes("*");
      });

      if (matchedPlaceOfService) {
        // if we find the * place of service, then we're gucci
        const copayAmount = matchedPlaceOfService.cost_share_data[0]?.copay_amount;
        return [new Decimal(copayAmount), undefined];
      } else {
        // if we don't find it, pick the lowest and highest
        let lowest = new Decimal(0);
        let highest = new Decimal(0);

        costShares.forEach((costShare, idx) => {
          const copayAmount = new Decimal(costShare.cost_share_data[0].copay_amount);
          if (idx === 0) {
            lowest = copayAmount;
          } else {
            lowest = Decimal.min(copayAmount, lowest);
          }
          highest = Decimal.max(copayAmount, highest);
        });
        return [lowest, highest];
      }
    }
  }
};

export const determineAccumulationMax = (
  memberEligibilityInfo: MemberEligibilityInfo,
  networkType: "in_network_tier_1" | "out_of_network",
  accumulatorType: "maximum_out_of_pocket" | "deductible",
  coverage_type: "subscriber_only" | "family",
): string | undefined => {
  const accumulators = memberEligibilityInfo.medical_plan_benefit?.accumulations?.accumulators || [];
  const applicableAccumulator = accumulators.find((accumulator) => {
    return accumulator.network_type === networkType && accumulator.type === accumulatorType;
  });
  const coverageAmounts = applicableAccumulator?.coverage_amounts || [];
  const applicableCoverageAmount = coverageAmounts.find((covAmnt) => {
    return covAmnt.coverage_type === coverage_type;
  });

  const coverageAmountKey = coverage_type === "subscriber_only" ? "individual_amount" : "family_amount";
  return applicableCoverageAmount?.[coverageAmountKey];
};

export const getAllowedPaymentMethods = (memberEligibilityInfo: MemberEligibilityInfo): string[] => {
  const configuredMethods = (memberEligibilityInfo.plan?.medicare_prescription_payment_plan_allowed_payment_methods || "")
    .split(",")
    .filter(Boolean);

  if (!configuredMethods.length) {
    // Assume all methods are allowed if the plan has no methods listed.
    return ["check", "card", "ach"];
  }

  return configuredMethods;
};
